import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import jwtDecode from 'jwt-decode';
import api from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('access_token'),
  isAuthenticated: !!localStorage.getItem('access_token'),
  loading: true,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const hasRunRef = useRef(false); // Guard for React Strict Mode

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const checkAndRefreshToken = async () => {
      if (process.env.NODE_ENV !== 'production') console.log('[Auth] 🔍 Checking authentication on app load...');
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (process.env.NODE_ENV !== 'production') console.log('[Auth] Tokens found:', { 
        hasAccessToken: !!accessToken, 
        hasRefreshToken: !!refreshToken,
        accessTokenLength: accessToken?.length || 0
      });

      if (!refreshToken) {
        if (process.env.NODE_ENV !== 'production') console.log('[Auth] ❌ No refresh token found, logging out');
        dispatch({ type: 'LOGOUT' });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      if (accessToken) {
        if (process.env.NODE_ENV !== 'production') console.log('[Auth] ✅ Access token found. Fetching user profile...');
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          const profileRes = await api.get('/api/profile');
          const user = profileRes.data;

          if (process.env.NODE_ENV !== 'production') console.log('[Auth] ✅ User fetched from profile:', user);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user,
              token: accessToken,
            },
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') console.error('[Auth] ❌ Failed to fetch user profile:', err);
if (process.env.NODE_ENV !== 'production') console.log('[Auth] 🔄 Attempting token refresh...');
          
          // Try to refresh the token
          try {
            const refreshResponse = await api.post('/api/auth/refresh', { 
              refresh_token: refreshToken 
            });
            const { access_token, refresh_token: newRefreshToken } = refreshResponse.data;
            
            if (process.env.NODE_ENV !== 'production') console.log('[Auth] ✅ Token refreshed successfully');
            localStorage.setItem('access_token', access_token);
            if (newRefreshToken) {
              localStorage.setItem('refresh_token', newRefreshToken);
            }
            
            // Try to fetch user profile again
            api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
            const profileRes = await api.get('/api/profile');
            const user = profileRes.data;
            
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user,
                token: access_token,
              },
            });
          } catch (refreshErr) {
            if (process.env.NODE_ENV !== 'production') console.error('[Auth] ❌ Token refresh failed:', refreshErr);
            dispatch({ type: 'LOGOUT' });
          }
        }
      } else {
        if (process.env.NODE_ENV !== 'production') console.log('[Auth] ❌ No access token found, logging out');
        dispatch({ type: 'LOGOUT' });
      }

      // ✅ Final step: turn off loading in all cases
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    checkAndRefreshToken();
  }, []);

  const login = async (credentials) => {
    try {
      if (process.env.NODE_ENV !== 'production') console.log('🔐 Attempting login...', { email: credentials.email });
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Send 'login' field to match backend expectation
      const loginData = {
        login: credentials.email,
        password: credentials.password,
      };
      if (process.env.NODE_ENV !== 'production') console.log('📤 Sending login data:', loginData);
      
      const response = await api.post('/api/auth/login', loginData);
      const { access_token, refresh_token, user } = response.data;
      
      if (process.env.NODE_ENV !== 'production') console.log('✅ Login successful:', { user: user.name, role: user.role });
      
      // Store tokens only if valid
      if (access_token && access_token !== 'undefined' && access_token !== 'null' && access_token !== '') {
        localStorage.setItem('access_token', access_token);
      }
      if (refresh_token && refresh_token !== 'undefined' && refresh_token !== 'null' && refresh_token !== '') {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      // Set token in API headers
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          token: access_token,
        },
      });
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('❌ Login failed:', error);
      const message = error.response?.data?.message || error.message || 'Login failed';
      toast.error(message);
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await api.post('/api/auth/register', userData);
      
      toast.success('Registration successful! Please login.');
      dispatch({ type: 'SET_LOADING', payload: false });
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      if (process.env.NODE_ENV !== 'production') console.log('🚪 Logging out...');
      // Call logout API if token exists
      const token = localStorage.getItem('access_token');
      if (token) {
        await api.post('/api/auth/logout');
        if (process.env.NODE_ENV !== 'production') console.log('✅ Logout API call successful');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('❌ Logout API error:', error);
    } finally {
      // Remove tokens from localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      // Remove token from API headers
      delete api.defaults.headers.common['Authorization'];
      
      dispatch({ type: 'LOGOUT' });
      toast.success('Logged out successfully');
      if (process.env.NODE_ENV !== 'production') console.log('✅ Logout completed');
    }
  };

  const updateUser = (userData) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }
      
      const response = await api.post('/api/auth/refresh', { 
        refresh_token: refreshTokenValue 
      });
      const { access_token, refresh_token } = response.data;
      
      // Only save valid tokens
      if (access_token && access_token !== 'undefined' && access_token !== 'null' && access_token !== '') {
        localStorage.setItem('access_token', access_token);
      }
      if (refresh_token && refresh_token !== 'undefined' && refresh_token !== 'null' && refresh_token !== '') {
        localStorage.setItem('refresh_token', refresh_token);
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return access_token;
    } catch (error) {
      logout();
      throw error;
    }
  };

  const value = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    login,
    register,
    logout,
    updateUser,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 